import { useForm, Link, Head } from '@inertiajs/react'
import Logo from '@/components/Logo'
import GoogleOAuthButton from '@/components/GoogleOAuthButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { FormEvent, ReactNode } from 'react'

type Props = {
  claim_event_id?: string | null
}

export default function OrganizationSignUp({ claim_event_id }: Props) {
  const { data, setData, post, processing, errors, transform } = useForm({
    first_name: '',
    last_name: '',
    title: '',
    name: '',
    email: '',
    ein: '',
    password: '',
    password_confirmation: '',
    cause_description: '',
    website: '',
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ organization: formData }))
    const url = claim_event_id
      ? `/organizations/sign_up?claim_event_id=${claim_event_id}`
      : '/organizations/sign_up'
    post(url)
  }

  return (
    <>
      <Head title="Organization Sign Up" />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-accent/10 to-primary/5 px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-semibold">Register your organization</CardTitle>
          <CardDescription>
            {claim_event_id
              ? 'Sign up to claim this event listing. Gala Season is for nonprofit organizations.'
              : 'Gala Season is for nonprofit organizations. Register to start hosting charitable events.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <GoogleOAuthButton
            accountType="organization"
            label="Sign up with Google"
            extraFields={claim_event_id ? { claim_event_id } : undefined}
          />
          <div className="relative">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-sm text-muted-foreground">
              or
            </span>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First name</Label>
                <Input
                  id="first_name"
                  value={data.first_name}
                  onChange={e => setData('first_name', e.target.value)}
                  autoFocus
                />
                {errors.first_name && (
                  <p className="text-base text-destructive">{errors.first_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last name</Label>
                <Input
                  id="last_name"
                  value={data.last_name}
                  onChange={e => setData('last_name', e.target.value)}
                />
                {errors.last_name && (
                  <p className="text-base text-destructive">{errors.last_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Your role</Label>
              <Input
                id="title"
                value={data.title}
                onChange={e => setData('title', e.target.value)}
                placeholder="e.g. Executive Director, Development Manager"
              />
              {errors.title && (
                <p className="text-base text-destructive">{errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Organization name</Label>
              <Input
                id="name"
                value={data.name}
                onChange={e => setData('name', e.target.value)}
              />
              {errors.name && (
                <p className="text-base text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={e => setData('email', e.target.value)}
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-base text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ein">EIN (Employer Identification Number)</Label>
              <Input
                id="ein"
                value={data.ein}
                onChange={e => setData('ein', e.target.value)}
                placeholder="XX-XXXXXXX"
              />
              <p className="text-xs text-muted-foreground">
                Your organization&apos;s federal tax ID. Gala Season is open to registered nonprofits only.
              </p>
              {errors.ein && (
                <p className="text-base text-destructive">{errors.ein}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={e => setData('password', e.target.value)}
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="text-base text-destructive">{errors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirmation">Confirm password</Label>
              <Input
                id="password_confirmation"
                type="password"
                value={data.password_confirmation}
                onChange={e => setData('password_confirmation', e.target.value)}
                autoComplete="new-password"
              />
              {errors.password_confirmation && (
                <p className="text-base text-destructive">{errors.password_confirmation}</p>
              )}
            </div>

            <div className="space-y-1 pt-2">
              <p className="text-sm font-medium text-muted-foreground">Optional</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cause_description">What's your cause?</Label>
                  <Textarea
                    id="cause_description"
                    value={data.cause_description}
                    onChange={e => setData('cause_description', e.target.value)}
                    placeholder="Tell us what your organization does and the causes you support..."
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={data.website}
                    onChange={e => setData('website', e.target.value)}
                    placeholder="https://yourorg.org"
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={processing}>
              {claim_event_id ? 'Sign Up & Claim Event' : 'Sign Up'}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-2 text-center text-base">
          <p>
            Already have an account?{' '}
            <Link
              href={claim_event_id ? `/organizations/sign_in?claim_event_id=${claim_event_id}` : '/organizations/sign_in'}
              className="underline underline-offset-4 hover:text-primary"
            >
              Sign in
            </Link>
          </p>
          <p>
            <Link href="/users/sign_up" className="underline underline-offset-4 hover:text-primary">
              Are you a user?
            </Link>
          </p>
        </CardFooter>
      </Card>
      </div>
    </div>
    </>
  )
}

OrganizationSignUp.layout = (page: ReactNode) => page
